import { FC, useContext, useEffect, useState } from 'react'
import { useQuery } from '@apollo/client'
import { Button, Table, Tag } from 'antd'
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons'
import classNames from 'classnames'
import { translate, translatePlural } from '../../../core'
import { PurchaseOrderMinMaxItemsQuery } from '../graphql/purchase-order.graphql'
import { PurchaseOrderFindMinMaxItemsResponse } from '../type/purchase-order'
import { PurchaseOrderEntryContext } from './PurchaseOrderEntryContext'
import styles from './PurchaseOrderEntry.module.scss'

export const PurchaseOrderEntryItemSearchStepMinMax: FC = () => {
  const {
    type,
    vendorIds,
    minMaxEnabled,
    minMaxExcludeItemIds,
    setMinMaxEnabled,
    setMinMaxItemExcluded,
  } = useContext(PurchaseOrderEntryContext)

  const [currentPage, setCurrentPage] = useState(0)
  const [pageSize, setPageSize] = useState(10)

  useEffect(() => setCurrentPage(0), [type, vendorIds])

  const { data, loading } = useQuery(PurchaseOrderMinMaxItemsQuery, {
    variables: {
      request: {
        take: pageSize,
        skip: currentPage * pageSize,
        type,
        vendorIds,
      },
    },
  })

  const total = data?.purchaseOrderMinMaxItems.count ?? 0

  const bannerText = loading
    ? ''
    : !total
      ? translate('min_max_none')
      : minMaxEnabled
        ? translate('min_max_in_cart')
        : translatePlural(total, 'min_max_below_minimum_one', 'min_max_below_minimum_many')

  return (
    <>
      <div className={classNames(styles.suggestBanner, minMaxEnabled && styles.suggestBannerActive)}>
        <span>{bannerText}</span>
        {minMaxEnabled
          ? (
            <Button size='small' onClick={() => setMinMaxEnabled(false)}>
              {translate('remove_all_from_cart')}
            </Button>
          )
          : (
            <Button size='small' type='primary' disabled={!total} onClick={() => setMinMaxEnabled(true)}>
              {translate('add_all_suggested')}
            </Button>
          )}
      </div>

      <Table<PurchaseOrderFindMinMaxItemsResponse>
        size='small'
        scroll={{ x: true }}
        rowKey='id'
        loading={loading}
        dataSource={data?.purchaseOrderMinMaxItems.data}
        onRow={row => minMaxEnabled && minMaxExcludeItemIds.includes(row.id) ? { className: styles.inactiveRow } : {}}
        columns={[
          { dataIndex: 'sku', title: translate('sku'), width: 110 },
          { dataIndex: 'description', title: translate('description') },
          { dataIndex: 'productCategory', title: translate('category') },
          {
            dataIndex: 'vendor',
            title: translate('vendor'),
            render: (vendor: string) => vendor && <Tag bordered={false}>{vendor}</Tag>,
          },
          { dataIndex: 'inStock', title: translate('on_hand', '&', 'display'), align: 'center' },
          { dataIndex: 'min', title: translate('min'), align: 'center' },
          { dataIndex: 'max', title: translate('max'), align: 'center' },
          {
            dataIndex: 'quantityNeeded',
            title: translate('suggested_qty'),
            align: 'center',
            render: (quantity: number) => <strong>{quantity}</strong>,
          },
          {
            key: 'action',
            width: 130,
            align: 'right',
            render: (_, row) => {
              if (!minMaxEnabled)
                return null

              return minMaxExcludeItemIds.includes(row.id)
                ? (
                  <Button size='small' icon={<PlusOutlined />} onClick={() => setMinMaxItemExcluded(row.id, false)}>
                    {translate('add')}
                  </Button>
                )
                : (
                  <span className={styles.addedCell}>
                    <Tag bordered={false} color='success'>{translate('added')}</Tag>
                    <Button
                      type='text'
                      size='small'
                      icon={<DeleteOutlined />}
                      aria-label={translate('remove')}
                      onClick={() => setMinMaxItemExcluded(row.id, true)}
                    />
                  </span>
                )
            },
          },
        ]}
        pagination={{
          total,
          current: currentPage + 1,
          pageSize,
          showSizeChanger: true,
          position: ['bottomCenter'],
        }}
        onChange={pagination => {
          if (pagination.current)
            setCurrentPage(pagination.current - 1)
          if (pagination.pageSize)
            setPageSize(pagination.pageSize)
        }}
      />
    </>
  )
}
